import type { FilterGroup, GroupLogic } from './filter';
import { buildSignalNarrowingGroup, SIGNAL_NARROWING_GROUP_ID } from './plays-view';
import { requestNarrowsSignals } from './signal-narrowing';

/**
 * U14 (plan `plans/2026-09-25-001-plays-tab-codex-fixes-plan.md`, finding
 * 5-1) — the "Pick which play first" Apply, as a pure function over the
 * rep's working filters. Shared (zero-dependency, like `signal-narrowing.ts`)
 * so the rule is unit-testable without rendering `ContactFlowTable`.
 *
 * Why Apply can be impossible: the filter model is FLAT — `group_logic`
 * combines every group at one level. Under `AND`, appending the one-play
 * narrowing group scopes the rep's filters (`(G1 AND G2) AND play`). Under
 * `OR` there is no way to say `(G1 OR G2) AND play`: appending gives
 * `G1 OR G2 OR play`, which WIDENS the rep's results instead of scoping
 * them, and the server's `rejectUnsortableField` guard 400s it anyway
 * (`requestNarrowsSignals` under `OR` needs EVERY group to narrow). So
 * under `OR` with another group in play the only honest answer is "can't".
 */

/**
 * True when the rep's filters are combined with `OR` and some group OTHER
 * than a previous narrowing group (which Apply replaces anyway) is in play.
 *
 * "In play" = the group has any condition at all (`conditions.length > 0`),
 * the same definition `requestNarrowsSignals` — and therefore the server
 * guard — uses for its "active groups". Deliberately NOT narrowed further
 * by `conditionIsActive`: a half-built chip (blank value) still counts on
 * the server, so under `OR` it still makes the appended request
 * non-narrowing and a 400. Using the looser `conditionIsActive` here would
 * let that case through to a silent no-op instead of the honest block.
 */
export function outerOrBlocksSignalScope(filters: FilterGroup[], groupLogic: GroupLogic): boolean {
  return groupLogic === 'OR'
    && filters.some(g => g.id !== SIGNAL_NARROWING_GROUP_ID && g.conditions.length > 0);
}

/**
 * The filters Apply would commit for play `name`, or `null` when no set of
 * filters built from the rep's own can scope them to that play:
 *   - `AND` (or `OR` with no other group in play) → the rep's groups,
 *     untouched, with the narrowing group REPLACING any previous one
 *     (never two) — today's behaviour.
 *   - `OR` with another group in play → `null` (see module comment).
 *
 * Post-condition (pinned by the tests, and enforced here rather than
 * trusted): every non-null result satisfies
 * `requestNarrowsSignals(result, groupLogic)` — e.g. a blank `name` yields
 * `null`, not a request the server would reject.
 */
export function applySignalScopeToFilters(
  filters: FilterGroup[],
  groupLogic: GroupLogic,
  name: string,
): FilterGroup[] | null {
  if (outerOrBlocksSignalScope(filters, groupLogic)) return null;
  const next = [
    ...filters.filter(g => g.id !== SIGNAL_NARROWING_GROUP_ID),
    buildSignalNarrowingGroup(name),
  ];
  return requestNarrowsSignals(next, groupLogic) ? next : null;
}
